import {
  Box,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { JsonValueEditor } from './JsonValueEditor'
import { useTranslation } from 'react-i18next'
import type { RuntimeJsonEditorKind } from './runtimeConfigJsonDescriptors'
import type { JsonObject, JsonValue } from './runtimeConfigJsonEditorUtils'

type RuntimeConfigJsonEditorProps = {
  value: JsonValue
  onChange: (value: JsonValue) => void
  editor: RuntimeJsonEditorKind
  readonlyPaths?: readonly string[]
}

const asObject = (value: JsonValue): JsonObject | null => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : null
)

const setPath = (root: JsonObject, path: string[], value: JsonValue): JsonObject => {
  const next = { ...root }
  let cursor: JsonObject = next
  path.forEach((segment, index) => {
    if (index === path.length - 1) {
      cursor[segment] = value
      return
    }
    const child = cursor[segment]
    cursor[segment] = child && typeof child === 'object' && !Array.isArray(child)
      ? { ...(child as JsonObject) }
      : {}
    cursor = cursor[segment] as JsonObject
  })
  return next
}

function GasThresholdEditor({ value, onChange }: Omit<RuntimeConfigJsonEditorProps, 'editor'>) {
  const { t } = useTranslation()
  const root = asObject(value)
  if (!root) return <JsonValueEditor value={value} onChange={onChange} />
  return (
    <VStack align="stretch" spacing={3}>
      {Object.entries(root).map(([network, chains]) => {
        const chainMap = asObject(chains)
        if (!chainMap) return null
        return (
          <Box key={network} borderWidth="1px" borderRadius="md" p={3}>
            <Text fontWeight="semibold" mb={2}>{network}</Text>
            <VStack align="stretch" spacing={2}>
              {Object.entries(chainMap).map(([chain, thresholds]) => {
                const values = asObject(thresholds)
                if (!values) return null
                return (
                  <SimpleGrid key={chain} columns={{ base: 1, md: 4 }} spacing={2} alignItems="end">
                    <Text fontSize="sm" fontWeight="medium">{chain}</Text>
                    {(['low', 'medium', 'high'] as const).map(level => (
                      <FormControl key={level}>
                        <FormLabel fontSize="xs" mb={1}>{t(`systemConfig.runtimeConfig.jsonEditor.levels.${level}`, { defaultValue: level })}</FormLabel>
                        <Input
                          size="sm"
                          type="number"
                          value={String(values[level] ?? '')}
                          onChange={event => {
                            const parsed = Number(event.target.value)
                            if (Number.isFinite(parsed)) {
                              onChange(setPath(root, [network, chain, level], parsed))
                            }
                          }}
                        />
                      </FormControl>
                    ))}
                  </SimpleGrid>
                )
              })}
            </VStack>
          </Box>
        )
      })}
    </VStack>
  )
}

function WalletThresholdEditor({ value, onChange, readonlyPaths }: Omit<RuntimeConfigJsonEditorProps, 'editor'>) {
  const { t } = useTranslation()
  const root = asObject(value)
  if (!root) return <JsonValueEditor value={value} onChange={onChange} readonlyPaths={readonlyPaths} />
  const thresholds = Array.isArray(root.thresholds) ? root.thresholds : []
  return (
    <VStack align="stretch" spacing={3}>
      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={2}>{t('systemConfig.runtimeConfig.jsonEditor.statusBands', { defaultValue: 'Status bands' })}</Text>
        <JsonValueEditor
          value={root.statusBands && typeof root.statusBands === 'object' && !Array.isArray(root.statusBands)
            ? root.statusBands as JsonObject
            : {}}
          onChange={statusBands => onChange({ ...root, statusBands })}
          readonlyPaths={readonlyPaths}
        />
      </Box>
      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={2}>{t('systemConfig.runtimeConfig.jsonEditor.thresholds', { defaultValue: 'Thresholds' })}</Text>
        <VStack align="stretch" spacing={2}>
          {thresholds.map((threshold, index) => (
            <Box key={index} borderWidth="1px" borderRadius="md" p={2}>
              <JsonValueEditor
                value={threshold}
                onChange={next => {
                  const nextThresholds = [...thresholds]
                  nextThresholds[index] = next
                  onChange({ ...root, thresholds: nextThresholds })
                }}
                readonlyPaths={readonlyPaths}
                path={`thresholds[${index}]`}
              />
            </Box>
          ))}
        </VStack>
      </Box>
    </VStack>
  )
}

function TokenPriceEditor({ value, onChange, readonlyPaths }: Omit<RuntimeConfigJsonEditorProps, 'editor'>) {
  const { t } = useTranslation()
  const root = asObject(value)
  if (!root) return <JsonValueEditor value={value} onChange={onChange} readonlyPaths={readonlyPaths} />
  const feeds = asObject(root.feeds)
  return (
    <VStack align="stretch" spacing={3}>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
        {(['cacheTtlSeconds', 'requestTimeoutMs', 'maxFutureSkewSeconds'] as const).map(key => (
          <FormControl key={key}>
            <FormLabel fontSize="xs" mb={1}>{key}</FormLabel>
            <Input
              size="sm"
              type="number"
              value={String(root[key] ?? '')}
              onChange={event => {
                const parsed = Number(event.target.value)
                if (Number.isFinite(parsed)) onChange({ ...root, [key]: parsed })
              }}
            />
          </FormControl>
        ))}
      </SimpleGrid>
      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={2}>{t('systemConfig.runtimeConfig.jsonEditor.feeds', { defaultValue: 'Feeds' })}</Text>
        {feeds ? (
          <VStack align="stretch" spacing={2}>
            {Object.entries(feeds).map(([token, feed]) => (
              <Box key={token} borderWidth="1px" borderRadius="md" p={2}>
                <Text fontSize="sm" fontWeight="medium" mb={1}>{token}</Text>
                <JsonValueEditor
                  value={feed}
                  onChange={next => onChange({ ...root, feeds: { ...feeds, [token]: next } })}
                  readonlyPaths={readonlyPaths}
                  path={`feeds.${token}`}
                />
              </Box>
            ))}
          </VStack>
        ) : (
          <Text color="gray.500" fontSize="sm">{t('systemConfig.runtimeConfig.jsonEditor.noFeeds', { defaultValue: 'No feeds configured' })}</Text>
        )}
      </Box>
    </VStack>
  )
}

export function RuntimeConfigJsonEditor(props: RuntimeConfigJsonEditorProps) {
  switch (props.editor) {
    case 'gasThresholds':
      return <GasThresholdEditor {...props} />
    case 'walletThresholds':
      return <WalletThresholdEditor {...props} />
    case 'tokenPrice':
      return <TokenPriceEditor {...props} />
    default:
      return <JsonValueEditor {...props} />
  }
}
